'use client'
import { getProfileDetail, updateNsfw, updateUserAgeGender } from "@/apis/profile"
import { AppConfigQueryOption } from "@/apis/query/common"
import { useUserDetailStore } from "@/store/useUserDetailStore"
import { useUserStore } from "@/store/useUserStore"
import { DarkButton } from "@/ui/Button"
import { cn, Modal, ModalBody, ModalContent, Switch } from "@heroui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useState } from "react"

const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || ''

// 年龄档位索引，与 APP/后端一致: 0:<14 1:14-17 2:18-20 3:21-23 4:24-26 5:26+
// 后端 AdultCensorshipPkg::underage = 2，即 index >= 2 (18+) 为成年人
const ADULT_AGE_INDEX = 2

/**
 * 年龄 + NSFW 设置弹窗（对齐 APP 端 UserAgeSettingPopupView）：
 * - 已登录且未设置年龄(age 为空或 <1)时打开页面强制弹出，不可关闭；
 * - <14: 显示"无可用内容"，无法进入；
 * - 14-17: NSFW 开关置灰不可选，并提示不会展示 NSFW 内容；
 * - 18+: NSFW 开关正常可选。
 */
export const AgeNsfwGate = () => {
  const t = useTranslations('ageGate')
  const queryClient = useQueryClient()
  const setDetail = useUserDetailStore(state => state.setDetail)
  const [isLogined, isPending] = useUserStore(state => [state.isLogined, state.isPending])

  const [ageIndex, setAgeIndex] = useState<number | null>(null)
  const [nsfwOn, setNsfwOn] = useState(false)

  // 与 useUserUpdateDetail 共享同一份缓存，避免依赖 persist store 造成误弹
  const detail = useQuery({
    queryKey: ['user', 'detail'],
    queryFn: () => getProfileDetail(),
    enabled: isLogined && !isPending,
    retry: false,
  })
  // 总开关，对齐 APP 端 age_open_dialog（配置 nsfw.age_enable）
  const appConfig = useQuery(AppConfigQueryOption)

  const save = useMutation({
    mutationFn: async () => {
      const res = await updateUserAgeGender({ age: ageIndex! })
      if (res.status !== 200) throw new Error((res as any).message || (res as any).msg)

      // 未成年人后端强制 nsfw=false（开 nsfw 会 412），仅成年人按开关值提交
      const nsfw = ageIndex! >= ADULT_AGE_INDEX && nsfwOn ? 1 : 0
      if (ageIndex! >= ADULT_AGE_INDEX) {
        await updateNsfw({ status: nsfw as 0 | 1 })
      }
      return { age: ageIndex!, nsfw }
    },
    onSuccess({ age, nsfw }) {
      setDetail({ age, nsfw })
      // 更新详情缓存，弹出条件不再满足自动关闭
      queryClient.setQueryData(['user', 'detail'], (old: any) =>
        old ? { ...old, data: { ...old.data, age, nsfw } } : old)
    }
  })

  const remoteAge = (detail.data?.data as any)?.age
  // 对齐 APP：age 为空或 <1（含 0 档，<14 无法使用）时弹出
  const shouldOpen =
    (appConfig.data?.age_open_dialog ?? true) &&
    detail.isSuccess && detail.data?.status === 200 &&
    (remoteAge === null || remoteAge === undefined || Number(remoteAge) < 1)

  if (!shouldOpen) return null

  const isUnderage14 = ageIndex === 0
  const isMinor = ageIndex !== null && ageIndex < ADULT_AGE_INDEX
  const ageOptions = [
    t('below', { num: 14 }),
    '14-17',
    '18-20',
    '21-23',
    '24-26',
    t('above', { num: 26 }),
  ]

  return (
    <Modal
      isOpen
      isDismissable={false}
      isKeyboardDismissDisabled
      hideCloseButton
      placement="center"
      classNames={{ base: "max-w-[420px]" }}
    >
      <ModalContent>
        <ModalBody className="py-8 px-6">
          <div className="text-center">
            <p className="text-[20px] font-bold text-theme-primary">{t('title', { name: APP_NAME })}</p>
            <p className={cn(
              "text-[13px] mt-2",
              isUnderage14 ? "text-theme-error font-bold" : "text-theme-primary/60"
            )}>
              {isUnderage14 ? t('noContent') : t('desc')}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-4">
            {ageOptions.map((label, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setAgeIndex(index)}
                className={cn(
                  "h-[40px] rounded-full text-[13px] font-bold transition-colors",
                  index === ageIndex
                    ? "bg-btn-primary text-theme-white"
                    : "bg-theme-primary/5 text-theme-primary/70 hover:bg-theme-primary/10"
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between">
              <span className={cn(
                "text-[14px] font-bold",
                isMinor ? "text-theme-primary/40" : "text-theme-primary"
              )}>{t('nsfwLabel')}</span>
              <Switch
                size="sm"
                aria-label={t('nsfwLabel')}
                isDisabled={ageIndex === null || isMinor}
                isSelected={!isMinor && nsfwOn}
                onValueChange={setNsfwOn}
              />
            </div>
            <p className="text-[12px] text-theme-primary/50 mt-1 min-h-[32px]">
              {isMinor ? t('nsfwMinor') : t('nsfwAdult')}
            </p>
          </div>

          {save.isError &&
            <p className="text-[12px] text-theme-error text-center">{save.error?.message}</p>}

          <DarkButton
            fullWidth
            isDisabled={ageIndex === null || isUnderage14}
            isLoading={save.isPending}
            onPress={() => save.mutate()}
          >
            {t('enter', { name: APP_NAME })}
          </DarkButton>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
